import * as React from "react";
import { CalendarCheck, CalendarX2, Clock, MapPin } from "lucide-react";

import {
  apiErrorMessage,
  cancelRollingAppointment,
  getRollingLinkManage,
  rescheduleRollingAppointment,
} from "@/api/client";
import { RollingLinkManageView } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { BookingCalendar, CalendarSlot } from "./BookingCalendar";
import { PublicLoadingLoop, useLoadingWindow } from "./public/PublicLoading";
import { PublicFooter } from "./public/PublicChrome";

/**
 * The invitee's tokenised self-service page for a rolling link appointment (reached from the cancel or
 * reschedule link in their confirmation email). Mirrors the ad-hoc PublicBookingManagePage — the
 * gradient shell, status badge, freed-up messaging — but adds an atomic reschedule: pick a new open
 * slot and the same appointment moves, no slot lost.
 */

type Load =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; view: RollingLinkManageView };

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Where the invitee is in the reschedule flow. Picking a slot only gets you as far as `confirming`:
 * the move is irreversible once sent, so it takes a deliberate second action. `done` is terminal,
 * because from there the fresh email is what carries the next set of actions.
 */
type Step = "view" | "picking" | "confirming" | "done";

export function PublicRollingLinkManagePage({ token }: { token: string }) {
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [step, setStep] = React.useState<Step>("view");
  const [pending, setPending] = React.useState<CalendarSlot | null>(null);
  const [movedFrom, setMovedFrom] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    getRollingLinkManage(token)
      .then((view) => setLoad({ status: "ready", view }))
      .catch(() => setLoad({ status: "error" }));
  }, [token]);

  const cancel = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const view = await cancelRollingAppointment(token);
      setLoad({ status: "ready", view });
      setStep("view");
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmReschedule = async (): Promise<void> => {
    if (!pending || load.status !== "ready") return;
    setBusy(true);
    setError(null);
    const from = load.view.start;
    try {
      const view = await rescheduleRollingAppointment(token, { new_start: pending.start });
      setLoad({ status: "ready", view });
      setMovedFrom(from);
      setPending(null);
      setStep("done");
    } catch (e) {
      // Stay on the review step so they can pick again: the slot may have gone in the meantime.
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // The calendar needs room to render readable date cells, so the shell widens for exactly the
  // step that shows one and drops back to the narrow card either side of it — the same narrow/wide
  // swap the two public booking pages make.
  const wide = step === "picking" && load.status === "ready" && load.view.open_slots.length > 0;

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue/10 via-background to-brand-teal/10 px-4 py-12">
      <div className={cn("mx-auto", wide ? "max-w-3xl" : "max-w-md")}>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_60px_-32px_rgba(15,23,42,0.28)]">
          {showLoading && (
            <PublicLoadingLoop />
          )}

          {!showLoading && load.status === "error" && (
            <div className="text-center">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <CalendarX2 className="h-6 w-6 text-muted-foreground" />
              </div>
              <h1 className="text-lg font-bold">We couldn't find this appointment</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                The link may be out of date. Check your confirmation email for the latest one.
              </p>
            </div>
          )}

          {!showLoading && load.status === "ready" && (
            <ManageBody
              view={load.view}
              step={step}
              pending={pending}
              movedFrom={movedFrom}
              busy={busy}
              error={error}
              onStartReschedule={() => {
                setError(null);
                setStep("picking");
              }}
              onPickSlot={(slot) => {
                setPending(slot);
                setStep("confirming");
              }}
              onBackToCalendar={() => {
                setError(null);
                setPending(null);
                setStep("picking");
              }}
              onCancelReschedule={() => {
                setError(null);
                setPending(null);
                setStep("view");
              }}
              onCancel={cancel}
              onConfirmReschedule={confirmReschedule}
            />
          )}
        </div>

        <PublicFooter className="mt-6" />
      </div>
    </div>
  );
}

function ManageBody({
  view,
  step,
  pending,
  movedFrom,
  busy,
  error,
  onStartReschedule,
  onPickSlot,
  onBackToCalendar,
  onCancelReschedule,
  onCancel,
  onConfirmReschedule,
}: {
  view: RollingLinkManageView;
  step: Step;
  pending: CalendarSlot | null;
  movedFrom: string | null;
  busy: boolean;
  error: string | null;
  onStartReschedule: () => void;
  onPickSlot: (slot: CalendarSlot) => void;
  onBackToCalendar: () => void;
  onCancelReschedule: () => void;
  onCancel: () => void;
  onConfirmReschedule: () => void;
}) {
  const cancelled = view.status === "CANCELLED";
  const upcoming = new Date(view.start).getTime() > Date.now();
  const canChange = !cancelled && upcoming;

  if (step === "picking") {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h1 className="text-lg font-bold">Pick a new time</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Currently {fmtDay(view.start)}, {fmtTime(view.start)}.
          </p>
        </div>
        {view.open_slots.length === 0 ? (
          <p className="rounded border-2 border-black bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            No other open times right now.
          </p>
        ) : (
          <BookingCalendar slots={view.open_slots} slotMinutes={slotMinutes(view)} onPick={onPickSlot} />
        )}
        <Button variant="outline" className="w-full" onClick={onCancelReschedule} disabled={busy}>
          Back
        </Button>
      </div>
    );
  }

  if (step === "confirming" && pending) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h1 className="text-lg font-bold">Confirm the new time</h1>
        </div>

        <div className="rounded-2xl bg-muted/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From</p>
          <p className="mt-1 text-sm text-muted-foreground line-through">
            {fmtDay(view.start)}, {fmtTime(view.start)} – {fmtTime(view.end)}
          </p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">To</p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Clock className="h-4 w-4" /> {fmtDay(pending.start)}, {fmtTime(pending.start)} –{" "}
            {fmtTime(pending.end)}
          </p>
        </div>

        {error && (
          <p className="rounded border-2 border-black bg-rose-50 p-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-200">
            {error}
          </p>
        )}

        <div className="space-y-2">
          <Button className="w-full" onClick={onConfirmReschedule} disabled={busy}>
            {busy ? "Moving…" : "Confirm"}
          </Button>
          <Button variant="outline" className="w-full" onClick={onBackToCalendar} disabled={busy}>
            Pick a different time
          </Button>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
            <CalendarCheck className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="text-lg font-bold">{view.title}</h1>
          <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            A fresh confirmation is on its way.
          </p>
        </div>

        <div className="rounded-2xl bg-muted/40 p-4">
          {movedFrom && (
            <p className="text-sm text-muted-foreground line-through">
              {fmtDay(movedFrom)}, {fmtTime(movedFrom)}
            </p>
          )}
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Clock className="h-4 w-4" /> {fmtDay(view.start)}
          </p>
          <p className="mt-0.5 pl-6 text-sm text-muted-foreground">
            {fmtTime(view.start)} – {fmtTime(view.end)}
          </p>
          {view.location && (
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" /> {view.location}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div
          className={cn(
            "mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full",
            cancelled ? "bg-muted" : "bg-emerald-100",
          )}
        >
          {cancelled ? (
            <CalendarX2 className="h-6 w-6 text-muted-foreground" />
          ) : (
            <CalendarCheck className="h-6 w-6 text-emerald-700" />
          )}
        </div>
        <h1 className={cn("text-lg font-bold", cancelled && "text-muted-foreground line-through")}>
          {view.title}
        </h1>
        <p className="mt-0.5 text-sm text-muted-foreground">with {view.host_name}</p>
      </div>

      <div className="rounded-2xl bg-muted/40 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Clock className="h-4 w-4" /> {fmtDay(view.start)}
        </p>
        <p className="mt-0.5 pl-6 text-sm text-muted-foreground">
          {fmtTime(view.start)} – {fmtTime(view.end)}
        </p>
        {view.location && (
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" /> {view.location}
          </p>
        )}
      </div>

      {error && (
        <p className="rounded border-2 border-black bg-rose-50 p-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-200">
          {error}
        </p>
      )}

      {cancelled ? (
        <p className="text-center text-sm text-muted-foreground">
          This appointment has been cancelled. The time has been freed up.
        </p>
      ) : canChange ? (
        <div className="space-y-2">
          <Button className="w-full" onClick={onStartReschedule} disabled={busy}>
            Reschedule
          </Button>
          <Button
            variant="outline"
            className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 dark:text-rose-300"
            onClick={onCancel}
            disabled={busy}
          >
            {busy ? "Cancelling…" : "Cancel this appointment"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            The host is notified and the slot reopens for someone else.
          </p>
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground">
          This appointment can no longer be changed here. Reply to your confirmation email if you
          need to.
        </p>
      )}
    </div>
  );
}

/** The rolling link's slot length, inferred from the appointment (for the reschedule calendar). */
function slotMinutes(view: RollingLinkManageView): number {
  return Math.max(5, Math.round((new Date(view.end).getTime() - new Date(view.start).getTime()) / 60000));
}
